import mockSchedule from '../mockData/mockSchedule';
import mockParticipants from '../mockData/mockParticipants';
import { apiClient } from './apiClient';

class ResultsService {
  constructor() {
    this.baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
  }

  // Simuliert API-Verzögerung (kann später entfernt werden)
  async delay(ms = 100) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // Requests laufen über den zentralen apiClient

  // Alle Ergebnisse abrufen
  async getAllResults(filters = {}) {
    try {
      await this.delay();
      
      const queryParams = new URLSearchParams();
      if (filters.category) queryParams.append('category', filters.category);
      if (filters.ageGroup) queryParams.append('ageGroup', filters.ageGroup);
      if (filters.gender) queryParams.append('gender', filters.gender);
      if (filters.venue) queryParams.append('venue', filters.venue);
      if (filters.status) queryParams.append('status', filters.status);
      
      const endpoint = `/results${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const result = await apiClient.get(endpoint);
      
      return {
        success: true,
        data: result,
        total: Array.isArray(result) ? result.length : 0
      };
    } catch (error) {
      return {
        success: false,
        error: error.message,
        data: []
      };
    }
  }

  // Ergebnisse gruppiert nach Events abrufen (für Frontend)
  async getResultsGroupedByEvents(filters = {}) {
    try {
      await this.delay();
      
      const queryParams = new URLSearchParams();
      if (filters.category) queryParams.append('category', filters.category);
      if (filters.ageGroup) queryParams.append('ageGroup', filters.ageGroup);
      if (filters.gender) queryParams.append('gender', filters.gender);
      if (filters.venue) queryParams.append('venue', filters.venue);
      if (filters.status) queryParams.append('status', filters.status);
      
      const endpoint = `/results/grouped${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const result = await apiClient.get(endpoint);
      
      return {
        success: true,
        data: result,
        total: Array.isArray(result) ? result.length : 0
      };
    } catch (error) {
      return {
        success: false,
        error: error.message,
        data: []
      };
    }
  }

  // Ergebnisse für eine spezifische Gruppe abrufen
  async getResultsByGroup(groupId) {
    try {
      await this.delay();
      const result = await apiClient.get(`/results/group/${groupId}`);
      return {
        success: true,
        data: result
      };
    } catch (error) {
      return {
        success: false,
        error: error.message,
        data: []
      };
    }
  }

  // Einzelnes Ergebnis abrufen
  async getResult(id) {
    try {
      await this.delay();
      
      const result = await apiClient.get(`/results/${id}`);
      
      return {
        success: true,
        data: result
      };
    } catch (error) {
      return {
        success: false,
        error: error.message,
        data: null
      };
    }
  }

  // Neues Ergebnis erstellen
  async createResult(resultData) {
    try {
      await this.delay();
      
      const result = await apiClient.post('/results', resultData);
      
      return {
        success: true,
        data: result
      };
    } catch (error) {
      return {
        success: false,
        error: error.message,
        data: null
      };
    }
  }

  // Ergebnis aktualisieren
  async updateResult(id, resultData) {
    try {
      await this.delay();
      
      const result = await apiClient.put(`/results/${id}`, resultData);
      
      return {
        success: true,
        data: result
      };
    } catch (error) {
      return {
        success: false,
        error: error.message,
        data: null
      };
    }
  }

  // Mehrere Ergebnisse für eine Gruppe aktualisieren
  async updateGroupResults(groupId, resultsData) {
    try {
      await this.delay();
      
      // Authorization-Header hinzufügen
      const headers = {
        'Content-Type': 'application/json'
      };
      
      if (typeof window !== 'undefined') {
        const token = localStorage.getItem('authToken');
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }
      }
      
      // Direkter Fetch, um die vollständige Response zu erhalten
      const response = await fetch(`${this.baseURL}/results/group/${groupId}/bulk`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ results: resultsData })
      });
      
      const responseData = await response.json();
      
      if (!response.ok) {
        return {
          success: false,
          error: responseData.message || responseData.error || `HTTP ${response.status}`,
          message: responseData.message || responseData.error || `HTTP ${response.status}`,
          data: null
        };
      }
      
      // Backend gibt { success: true, message, data } zurück
      if (responseData && responseData.success !== undefined) {
        return responseData;
      }
      
      // Fallback: Verpacke in success-Objekt
      return {
        success: true,
        data: responseData,
        message: 'Ergebnisse erfolgreich gespeichert'
      };
    } catch (error) {
      console.error('Fehler in updateGroupResults:', error);
      return {
        success: false,
        error: error.message || 'Unbekannter Fehler',
        message: error.message || 'Unbekannter Fehler',
        data: null
      };
    }
  }

  // Ergebnis löschen
  async deleteResult(id) {
    try {
      await this.delay();
      
      const result = await apiClient.delete(`/results/${id}`);
      
      return {
        success: true,
        data: result
      };
    } catch (error) {
      return {
        success: false,
        error: error.message,
        data: null
      };
    }
  }

  // Ergebnisse für eine Gruppe generieren
  async generateResultsForGroup(groupId) {
    try {
      await this.delay();
      const result = await apiClient.post(`/results/group/${groupId}/generate`);
      return {
        success: true,
        data: result
      };
    } catch (error) {
      return {
        success: false,
        error: error.message,
        data: null
      };
    }
  }

  // Fallback auf Mock-Daten bei API-Fehlern
  async getMockResults() {
    const mockResults = mockSchedule.map(event => {
      // Finde Teilnehmer für dieses Event
      const eventParticipants = mockParticipants.filter(p =>
        p.category.includes(event.category) &&
        p.ageGroup === event.ageGroup &&
        p.gender.toLowerCase() === event.gender.toLowerCase()
      );

      // Generiere zufällige Punkte für jeden Teilnehmer
      const participantsWithScores = eventParticipants.map((participant, index) => {
        // Generiere Punkte für 5 Wertungsrichter (zwischen 7.0 und 9.5)
        const judgeScores = Array.from({ length: 5 }, () => 
          parseFloat((Math.random() * 2.5 + 7.0).toFixed(2))
        );
        
        // Generiere ein zufälliges Penalty (zwischen -0.5 und +0.5, meist 0)
        const penalty = Math.random() < 0.3 ? parseFloat((Math.random() - 0.5).toFixed(2)) : 0;
        
        // Berechne Durchschnitt aus allen 5 Jury-Punkten
        const averageScore = parseFloat((judgeScores.reduce((sum, score) => sum + score, 0) / judgeScores.length).toFixed(2));
        
        // Berechne finale Punktzahl mit Penalty
        const finalScore = parseFloat((averageScore + penalty).toFixed(2));
        
        return {
          id: participant.id,
          name: participant.name,
          nation: participant.nation,
          nationCode: participant.nationCode,
          judgeScores: judgeScores,
          penalty: penalty,
          score: finalScore,
          rank: index + 1
        };
      });

      // Sortiere nach Punkten (höchster zuerst) und weise korrekte Ränge zu
      const sortedParticipants = participantsWithScores
        .sort((a, b) => b.score - a.score)
        .map((participant, index) => ({
          ...participant,
          rank: index + 1
        }));

      return {
        id: event.id,
        eventName: event.event,
        location: event.location,
        category: event.category,
        ageGroup: event.ageGroup,
        gender: event.gender,
        participants: sortedParticipants
      };
    });

    return {
      success: true,
      data: mockResults,
      total: mockResults.length
    };
  }

  // Hilfsfunktion: Konvertiere Frontend-Datenformat zu Backend-Format
  convertToBackendFormat(frontendResults) {
    return frontendResults.map(result => ({
      participantId: result.id,
      judgeScores: result.judgeScores,
      penalty: result.penalty
    }));
  }

  // Hilfsfunktion: Konvertiere Backend-Datenformat zu Frontend-Format
  convertToFrontendFormat(backendResults) {
    return backendResults.map(result => ({
      id: result.participant.id,
      name: result.participant.name,
      nation: result.participant.nation.name,
      nationCode: result.participant.nation.code,
      judgeScores: [
        parseFloat(result.judgeScore1),
        parseFloat(result.judgeScore2),
        parseFloat(result.judgeScore3),
        parseFloat(result.judgeScore4),
        parseFloat(result.judgeScore5)
      ],
      penalty: parseFloat(result.penalty),
      score: parseFloat(result.finalScore),
      rank: result.rank
    }));
  }
}

// Singleton-Instanz exportieren
const resultsService = new ResultsService();
export default resultsService; 